<template>
  <Doughnut
    :chart-data="convertedData"
    :chart-options="mergedOptions"
    :chart-id="chartId"
    :dataset-id-key="datasetIdKey"
    :plugins="convertedPlugins"
    :css-classes="cssClasses"
    :styles="styles"
    :width="width"
    :height="height" />
</template>

<script>
  import { Doughnut } from 'vue-chartjs'
  import { Chart as ChartJS, Title, Tooltip, Legend, ArcElement, CategoryScale } from 'chart.js'

  import { merge, deepCopy } from 'utils/json'

  import { hexToRgba } from 'utils/colorcode'

  ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale)

  const LOCAL_CHART_OPTIONS = {
  };

  export default {
    name: 'DoughnutChart',
    components: { Doughnut },
    props: {
      chartData: {
        type: Object,
        default: null,
      },
      chartOptions: {
        type: Object,
        default: null,
      },
      chartId: {
        type: String,
        default: 'doughnut-chart',
      },
      datasetIdKey: {
        type: String,
        default: 'label',
      },
      width: {
        type: Number,
        default: 0,
      },
      height: {
        type: Number,
        default: 0,
      },
      cssClasses: {
        default: '',
        type: String,
      },
      styles: {
        type: Object,
        default: () => { },
      },
      plugins: {
        type: Object,
        default: [],
      },
      centerLabelVisible: {
        type: Boolean,
        default: false,
      },
      centerLabelParameter: {
        type: Object,
        default: {

        },
      },
      percentageChart: {
        type: Boolean,
        default: false,
      },
      chartBackgroundVisible: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      isHorizontalChart() {
        return this.mergedOptions.indexAxis === "y";
      },
    },
    methods: {
      convertData() {
        this.convertedData = deepCopy(this.chartData);

        if (this.percentageChart === false) return;

        if (!Object.keys(this.convertedData).some((k) => k === "datasets")) return;

        let totalDataArray = [];
        this.convertedData.datasets.forEach((d) => {
          if (Object.keys(d).some((k) => k === "data")) {
            totalDataArray.push(d.data.reduce((prev, current) => prev + current));
          }
        });

        this.convertedData.datasets.forEach((d, index) => {
          if (Object.keys(d).some((k) => k === "data")) {
            d.data = d.data.map((i) => {
              return Number((i / totalDataArray[index] * 100).toFixed(1));
            });
          }
        });
      },
      convertOptions() {
        this.mergedOptions = merge(this.chartOptions, LOCAL_CHART_OPTIONS);

        if (this.percentageChart === false) return;
        if (!this.mergedOptions.scales) this.mergedOptions.scales = {};
        if (!this.mergedOptions.scales.x) this.mergedOptions.scales.x = {};
        this.mergedOptions.scales.x.min = 0;
        this.mergedOptions.scales.x.max = 100;
        if (!this.mergedOptions.scales.y) this.mergedOptions.scales.y = {};
        this.mergedOptions.scales.y.min = 0;
        this.mergedOptions.scales.y.max = 100;
      },
      convertPlugins() {
        this.convertedPlugins = Array.isArray(this.plugins) ? this.plugins : [this.plugins];

        const centerLabel = {
          id: "centerLabel",
          beforeDraw: function (chart) {
            const { ctx, chartArea: { top, right, bottom, left, width, height } } = chart;

            let textList = [`${chart.data.labels[0]}`, `${chart.data.datasets[0].data[0]}%`];
            let fontSize = 0.00;
            if (height < width) {
              fontSize = (height / (4 * (textList.length + 1))).toFixed(1);
            } else {
              fontSize = (width / (4 * (textList.length + 1))).toFixed(1);
            }
            ctx.font = fontSize + "px sans-serif";
            ctx.lineheight = fontSize + "px";
            ctx.textBaseline = "middle";
            ctx.textAlign = "center";
            textList.forEach((text, index) => {
              let isEven = textList.length % 2 === 0;
              let base = isEven ? textList.length / 2 : (textList.length - 1) / 2;
              let offset = isEven
                ? -(base - 0.5) + index
                : index - base;
                ctx.fillText(text, (width / 2), (height / 2) + (fontSize * offset));
            });
            ctx.save();
          }
        };

        if (this.centerLabelVisible) this.convertedPlugins.push(centerLabel);

        const chartBackground = {
          id: "chartBackground",
          beforeDraw: function (chart) {
            const { ctx, data, chartArea: { width, height } } = chart;
            console.log(chart);
            ctx.save();

            ctx.fillStyle = hexToRgba(__COLOR_CODE.secondary, 0.15);
            ctx.strokeStyle = hexToRgba(__COLOR_CODE.secondary, 0.15);

            data.datasets.forEach((ds, index) => {
              let d = chart.getDatasetMeta(index).data[0];
              ctx.lineWidth = d.outerRadius - d.innerRadius;
              ctx.beginPath();
              ctx.arc(width / 2, height / 2, (d.innerRadius + d.outerRadius) / 2, 0, 2 * Math.PI);
              ctx.closePath();
              ctx.stroke();
            });

            ctx.restore();
          }
        };

        if (this.chartBackgroundVisible) this.convertedPlugins.push(chartBackground);
      },
      rewriteTooltips() {
        const convertTooltipLabel = (tooltipItem) => {
          const datasetIndex = tooltipItem.datasetIndex;
          const index = tooltipItem.dataIndex;
          const datasetLabel = this.convertedData.labels[index];
          const originalValue = this.chartData.datasets[datasetIndex]?.data[index];
          const rateValue = this.convertedData.datasets[datasetIndex]?.data[index];

          return `${datasetLabel}: ${rateValue}% (${originalValue})`;
        };

        if (this.percentageBarChart === false) return;
        if (!this.mergedOptions.plugins) this.mergedOptions.plugins = {};
        if (!this.mergedOptions.plugins.tooltip) this.mergedOptions.plugins.tooltip = {};
        if (!this.mergedOptions.plugins.tooltip.callbacks) this.mergedOptions.plugins.tooltip.callbacks = {};
        this.mergedOptions.plugins.tooltip.callbacks.label = convertTooltipLabel;
      },
    },
    data: () => {
      return {
        convertedData: {},
        mergedOptions: {},
        convertedPlugins: [],
      }
    },
    created() {
      this.convertData();
      this.convertOptions();
      this.convertPlugins();

      if (this.percentageChart) this.rewriteTooltips();
    },
    watch: {
      chartData: {
        handler: function () {
          this.convertData();
        },
        deep: true,
      },
      chartOptions: {
        handler: function () {
          this.convertOptions();
        },
        deep: true,
      },
    },
  }
</script>
